import React from 'react';
import { createPortal } from 'react-dom';

interface ContactRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  tutorName: string;
  isSuccess: boolean;
  message?: string;
}

export const ContactRequestModal: React.FC<ContactRequestModalProps> = ({ 
  isOpen, 
  onClose, 
  tutorName,
  isSuccess,
  message
}) => {
  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 bg-white bg-opacity-95 flex items-center justify-center z-[9999] p-4 md:p-8 overflow-y-auto">
      <div className="w-full max-w-4xl mx-auto text-center py-8">
        {/* Bouton fermer */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <i className="fas fa-times text-3xl"></i>
        </button>

        {/* Header */}
        <div className="mb-12">
          <div className={`mx-auto h-32 w-32 rounded-full flex items-center justify-center mb-8 ${
            isSuccess ? 'bg-green-100' : 'bg-red-100'
          }`}>
            <i className={`fas ${
              isSuccess && message?.includes('Like') ? 'fa-graduation-cap text-green-600' : isSuccess ? 'fa-paper-plane text-green-600' : 'fa-exclamation-triangle text-red-600'
            } text-5xl`}></i>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
            {isSuccess && message?.includes('Like') ? '🎓 Intérêt manifesté !' : isSuccess ? 'Demande envoyée !' : 'Erreur'}
          </h1>
          {isSuccess ? (
            message ? (
              <p className="text-lg md:text-2xl text-gray-600 px-4">
                {message.split(tutorName).map((part, index, array) => (
                  <React.Fragment key={index}>
                    {part}
                    {index < array.length - 1 && <span className="font-semibold text-blue-600">{tutorName}</span>}
                  </React.Fragment>
                ))}
              </p>
            ) : (
              <p className="text-lg md:text-2xl text-gray-600 px-4">
                Votre demande de contact a été envoyée à{' '}
                <span className="font-semibold text-blue-600">{tutorName}</span>.
              </p>
            )
          ) : (
            <p className="text-lg md:text-2xl text-gray-600 px-4">
              {message || 'Une erreur est survenue lors de l\'envoi de votre demande.'}
            </p>
          )}
        </div>

        {isSuccess && (
          /* Success Info */
          <div className="mb-8 md:mb-12 bg-blue-50 border-2 border-blue-200 rounded-2xl p-6 md:p-8 max-w-2xl mx-auto">
            <h3 className="text-lg md:text-xl font-semibold text-blue-900 mb-4">
              <i className="fas fa-info-circle mr-2"></i>
              Prochaines étapes
            </h3>
            <ul className="space-y-3 text-base md:text-lg text-blue-800 text-left max-w-xl mx-auto">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>{message?.includes('Like') ? 'Le tuteur ou la tutrice recevra votre manifestation d\'intérêt dans son tableau de bord' : 'Le tuteur recevra votre demande dans son tableau de bord'}</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>{message?.includes('Like') ? 'Il ou elle pourra manifester son intérêt en retour' : 'Il pourra accepter ou refuser votre demande'}</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Vous serez notifié de sa réponse</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>{message?.includes('Like') ? 'Si match mutuel, vous aurez accès à ses informations de contact' : 'Si acceptée, vous aurez accès à ses informations de contact'}</span>
              </li>
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col md:flex-row gap-4 justify-center items-center px-4">
          <button
            onClick={onClose}
            className={`w-full md:w-auto px-8 md:px-12 py-3 md:py-4 font-semibold rounded-xl transition-colors text-lg md:text-xl ${
              isSuccess 
                ? 'bg-green-600 text-white hover:bg-green-700' 
                : 'bg-gray-600 text-white hover:bg-gray-700'
            }`}
          >
            <i className={`fas ${isSuccess ? 'fa-check' : 'fa-times'} mr-2 md:mr-3`}></i>
            {isSuccess ? 'Parfait !' : 'Fermer'}
          </button>
          
          {isSuccess && (
            <button
              onClick={() => window.location.href = '/profile/requests'}
              className="w-full md:w-auto px-6 md:px-8 py-3 md:py-4 border-2 border-[#002c46] text-[#002c46] font-semibold rounded-xl hover:bg-[#002c46]/5 transition-colors text-base md:text-lg"
            >
              <i className="fas fa-envelope mr-2"></i>
              Voir mes demandes
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Utiliser createPortal pour afficher le modal au niveau du document
  return createPortal(modalContent, document.body);
};